import { useEffect, useRef, useState } from 'react';

type LazyQueryHooks<QueryArg, ResultType> = {
  useLazyQuery: (
    arg?: QueryArg,
    preferCacheValue?: boolean,
  ) => [
    (arg: QueryArg, preferCacheValue?: boolean) => any,
    { data?: ResultType; originalArgs?: QueryArg; isSuccess: boolean; isFetching: boolean },
    ...any[],
  ];
};

type GetResultTypeFromEndpoint<Endpoint> =
  Endpoint extends LazyQueryHooks<any, infer ResultType> ? ResultType : never;

type GetQueryArgTypeFromEndpoint<Endpoint> =
  Endpoint extends LazyQueryHooks<any, infer QueryArg> ? QueryArg : never;

type GetNodeTypeFromEndpoint<Endpoint> =
  GetResultTypeFromEndpoint<Endpoint> extends { nodes: infer NodeType } ? NodeType : never;
interface UseInfiniteQueryOptions</* ResultType, */ QueryArg /* , NodeType */> {
  // getNextPageParams(args: QueryArg, lastResult: ResultType): QueryArg | null; // turn this on and add a default if not every endpoint follows the Request.AllListParams interface
  // getNodesFromResultData(result: ResultType): NodeType[]; // Turn this on and add a default if not every endpoint return the list in the `nodes` property
  params: QueryArg;
}

function useInfiniteQuery<
  Endpoint extends LazyQueryHooks<any, any>,
  ResultType = GetResultTypeFromEndpoint<Endpoint>,
  QueryArg = GetQueryArgTypeFromEndpoint<Endpoint>,
  NodeType = GetNodeTypeFromEndpoint<Endpoint>,
>(
  endpoint: Endpoint,
  options: UseInfiniteQueryOptions</* ResultType,  */ QueryArg /* , NodeType */>,
) {
  const nextPageParams = useRef<(QueryArg & { offset?: number }) | null>(null);
  const prevFiltersParams = useRef<QueryArg | null>(null);
  const shouldReset = useRef(false);
  const [nodes, setNodes] = useState<NodeType[]>([]);
  const [trigger, result] = endpoint.useLazyQuery();

  useEffect(() => {
    const currentFilters = options.params;
    const prevFilters = prevFiltersParams.current;
    const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(prevFilters);

    if (filtersChanged) {
      shouldReset.current = true;
      prevFiltersParams.current = currentFilters;
    }
    trigger(options.params);
  }, [options.params]);

  useEffect(() => {
    if (!result.isSuccess) {
      return;
    }

    const newNodes = result.data.nodes;
    // nextPageParams.current = options.getNextPageParams(result.originalArgs, result.data);
    nextPageParams.current =
      newNodes.length === result.originalArgs.size
        ? { ...result.originalArgs, offset: result.originalArgs.offset + result.originalArgs.size }
        : null;
    // const newNodes = options.getNodesFromResultData(result.data);
    setNodes(newNodes);
  }, [result.data]);

  const totalFetched = nextPageParams.current?.offset ?? 0;

  return {
    ...result,
    data: {
      ...(result.data as ResultType),
      nodes: import.meta.env.MODE === 'test' ? ((result?.data?.nodes ?? []) as NodeType[]) : nodes,
    },
    nodes,
    isLoading: result.isFetching && nodes.length === 0,
    hasNextPage: nextPageParams.current !== null,
    totalLength: totalFetched + 1,
    totalFetched,
    fetchNextPage() {
      if (nextPageParams.current !== null) {
        trigger(nextPageParams.current, true);
      }
    },
  };
}
export default useInfiniteQuery;
